import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getMe, checkSetup } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

/**
 * Session management constants (NIS2 / ISO 27001).
 * Idle timeout: 30 min — auto-logout if no user interaction.
 * Absolute timeout: 8 hours — hard limit on session length.
 * Warning: shown 2 minutes before idle expiry.
 * Refresh interval: every 20 minutes (while user is active).
 */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;        // 30 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000;       // 2 minutes
const REFRESH_INTERVAL_MS = 20 * 60 * 1000;    // 20 minutes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  /* Session state */
  const [sessionWarning, setSessionWarning] = useState(false);
  const [sessionExpiredReason, setSessionExpiredReason] = useState('');
  const lastActivity = useRef(Date.now());
  const idleTimer = useRef(null);
  const warningTimer = useRef(null);
  const refreshTimer = useRef(null);
  const absoluteEnd = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let isInitialized = true;
      try {
        const setupRes = await checkSetup();
        isInitialized = setupRes.data?.initialized ?? false;
      } catch (err) {
        if (!err.response) isInitialized = true;
        else if (err.response.status === 404) isInitialized = true;
        else isInitialized = false;
      }

      if (!token) {
        setNeedsSetup(!isInitialized);
        setLoading(false);
        return;
      }

      try {
        // Cold-start tolerance: backend container may still be booting (Mongo TLS
        // handshake, scheduler startup). One quick retry before bouncing the user
        // back to login on a network/timeout error (not on 401).
        let response;
        try {
          response = await getMe();
        } catch (firstErr) {
          const isAuthFailure = firstErr.response && firstErr.response.status === 401;
          if (isAuthFailure) throw firstErr;
          // Network error or 5xx — wait 2s and retry once
          await new Promise(r => setTimeout(r, 2000));
          response = await getMe();
        }
        setUser(response.data);
        setNeedsSetup(false);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setNeedsSetup(!isInitialized);
      }
    } catch {
      const token = localStorage.getItem('token');
      setNeedsSetup(!token);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  /* ---- Login / Logout ---- */
  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setNeedsSetup(false);
    setSessionWarning(false);
    setSessionExpiredReason('');
    lastActivity.current = Date.now();
    /* Fetch absolute timeout from backend */
    api.get('/auth/session-info').then(({ data }) => {
      if (data.absolute_remaining_seconds != null) {
        absoluteEnd.current = Date.now() + data.absolute_remaining_seconds * 1000;
      }
    }).catch(() => {});
  }, []);

  const logout = useCallback((reason) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setSessionWarning(false);
    absoluteEnd.current = null;
    if (reason) {
      setSessionExpiredReason(reason);
    }
    window.location.href = '/login';
  }, []);

  const clearSessionExpired = useCallback(() => setSessionExpiredReason(''), []);

  /* ---- Idle detection ---- */
  const resetIdleTimers = useCallback(() => {
    lastActivity.current = Date.now();
    setSessionWarning(false);

    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (idleTimer.current) clearTimeout(idleTimer.current);

    // Show warning 2 min before idle timeout
    warningTimer.current = setTimeout(() => {
      setSessionWarning(true);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Auto-logout at idle timeout
    idleTimer.current = setTimeout(() => {
      logout('Your session expired due to inactivity.');
    }, IDLE_TIMEOUT_MS);
  }, [logout]);

  /* ---- Token refresh ---- */
  const refreshToken = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    // Check absolute timeout
    if (absoluteEnd.current && Date.now() >= absoluteEnd.current) {
      logout('Your session has reached the maximum duration (8 hours). Please log in again.');
      return;
    }
    try {
      const { data } = await api.post('/auth/refresh');
      if (data.token) {
        localStorage.setItem('token', data.token);
      }
    } catch {
      // Refresh failed — token likely expired
      logout('Your session expired. Please log in again.');
    }
  }, [logout]);

  /* ---- Lifecycle: attach activity listeners + timers ---- */
  useEffect(() => {
    if (!user) return;

    const onActivity = () => {
      lastActivity.current = Date.now();
      // Check absolute timeout on each activity
      if (absoluteEnd.current && Date.now() >= absoluteEnd.current) {
        logout('Your session has reached the maximum duration (8 hours). Please log in again.');
        return;
      }
      resetIdleTimers();
    };

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    resetIdleTimers();

    // Periodic token refresh
    refreshTimer.current = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivity.current;
      if (timeSinceActivity < IDLE_TIMEOUT_MS) {
        refreshToken();
      }
    }, REFRESH_INTERVAL_MS);

    // Fetch session info on mount
    api.get('/auth/session-info').then(({ data }) => {
      if (data.absolute_remaining_seconds != null) {
        absoluteEnd.current = Date.now() + data.absolute_remaining_seconds * 1000;
      }
    }).catch(() => {});

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
      clearTimeout(idleTimer.current);
      clearTimeout(warningTimer.current);
      clearInterval(refreshTimer.current);
    };
  }, [user, resetIdleTimers, refreshToken, logout]);

  const value = {
    user,
    loading,
    needsSetup,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    sessionWarning,
    sessionExpiredReason,
    clearSessionExpired,
    keepAlive: resetIdleTimers,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
